import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { CtaBand } from "../components/site/CtaBand";
import { Reveal, EASE } from "../lib/motion";
import { useSEO, useLenis } from "../lib/hooks";
import { CORE, MEMBERS } from "../data/team";

const Team = () => {
  useLenis();
  useSEO({
    title: "The JSC Team — People Behind ForesightFlow, Jay Shah Consultancy",
    description:
      "Meet the Jay Shah Consultancy team building ForesightFlow — the analysts and engineers delivering business intelligence and data analytics for Indian fashion retail.",
    keywords:
      "ForesightFlow team, Jay Shah Consultancy team, business intelligence Pune, data analytics consultancy India, fashion retail analytics",
  });

  return (
    <div className="jsc-grain relative bg-[#FAF8F5] min-h-screen" data-testid="page-team">
      <Navbar />

      {/* HERO with wall image */}
      <section className="relative pt-24 md:pt-28 overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <Reveal>
            <div className="relative rounded-[4px] overflow-hidden bg-[#1C1C1E]">
              <img
                src="/assets/jsc-wall.jpg"
                alt="JSC office wall logo — Jay Shah Consultancy"
                width="1210" height="860" loading="eager"
                className="w-full h-auto object-contain"
              />
            </div>
          </Reveal>

          <div className="mt-10 md:mt-14 max-w-4xl">
            <Reveal>
              <p className="font-body text-xs md:text-sm uppercase tracking-[0.24em] text-[#1C1C1E] mb-6 flex items-center gap-3">
                <span className="inline-block w-8 h-px bg-[#B39B77]" />
                The JSC Team
              </p>
            </Reveal>
            <h1 data-testid="team-headline" className="font-display font-semibold text-[#1C1C1E] text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              A small team that reads retail data for a living.
            </h1>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-2xl font-body text-lg md:text-xl leading-relaxed text-[#3E4A5C]">
                We&apos;re the analysts and engineers behind ForesightFlow. Want the backstory?{" "}
                <Link to="/about" className="text-[#1C1C1E] link-underline font-medium">
                  Read why Jay Shah started JSC
                </Link>
                .
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section id="team" className="relative py-20 md:py-28 mt-6 border-t border-[rgba(28,28,30,0.08)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          {/* PART A — Core Team */}
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-[#B39B77] mb-4">The core team</p>
            <h2 className="font-display font-semibold text-[#1C1C1E] text-3xl md:text-5xl tracking-tight max-w-2xl">
              The people building JSC.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {CORE.map((m, i) => (
              <motion.div
                key={m.name}
                data-testid={`core-card-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, ease: EASE, delay: i * 0.08 }}
                className="group"
              >
                <div className="relative aspect-[4/5] rounded-[3px] overflow-hidden bg-[#1C1C1E]">
                  <img
                    src={m.img}
                    alt={m.alt}
                    width="943" height="1178" loading="lazy"
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1E]/75 via-transparent to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5">
                    <h3 className="font-display text-xl text-[#FAF8F5] leading-tight">{m.name}</h3>
                    <p className="mt-1 font-body text-xs uppercase tracking-[0.16em] text-[#B39B77]">{m.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* PART B — Team Members */}
          <div className="mt-16 md:mt-20">
            <Reveal>
              <p className="font-body text-xs uppercase tracking-[0.24em] text-[#3E4A5C] mb-6">Team members</p>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
              {MEMBERS.map((m, i) => (
                <Reveal key={m.name} delay={i * 0.08}>
                  <div data-testid={`member-card-${i}`} className="flex items-center gap-4 bg-[#FAF8F5] border border-[rgba(28,28,30,0.1)] rounded-[3px] p-5">
                    <span className="w-12 h-12 shrink-0 rounded-full bg-[#1C1C1E] flex items-center justify-center font-display text-base text-[#B39B77]">{m.initials}</span>
                    <div>
                      <h3 className="font-display text-lg text-[#1C1C1E] leading-tight">{m.name}</h3>
                      <p className="font-body text-xs uppercase tracking-[0.14em] text-[#3E4A5C]">{m.role}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* PART C — Closing group statement */}
          <Reveal delay={0.1}>
            <p className="mt-14 max-w-3xl font-display italic text-2xl md:text-3xl leading-snug text-[#1C1C1E]">
              …and our growing team of Business and Data Analysts, working behind the scenes on every
              dashboard and every insight.
            </p>
          </Reveal>
        </div>
      </section>

      <CtaBand eyebrow="Say hello" title="Want to meet the team behind ForesightFlow?" testid="team-cta" />
      <Footer />
    </div>
  );
};

export default Team;
